import React, {ReactElement, useEffect} from 'react';
import {DragDropContext, Draggable, DraggingStyle, Droppable, NotDraggingStyle} from 'react-beautiful-dnd';
import {useSelector} from 'react-redux';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {IElementSequenceQuestion} from '../../../../../incase-domain/elements/question/questionSequence';
import {SequenceQuestionInteraction} from '../../../../../incase-domain/proptypes/runfile';
import {loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {swapArrayIndices} from '../../../../../util/array';
import {getAnswerStyles} from '../common/getStyles';
import QuestionLayout from '../common/QuestionLayout';
import {handleSelectAnswers} from '../utils/handleTryButton.question.utils';
import useSequenceQuestionTry from './useSequenceQuestionTry';
import QuestionAnswer, {AnswerTypes} from '../question/QuestionAnswer';
import {useOpenComment} from '../common/useOpenComment';

interface SequenceQuestionElementProps {
    element: IElementSequenceQuestion;
    elementInteractions: SequenceQuestionInteraction;
    savePresentationRunFile: (elementRunFile: Record<string, unknown>, newPoints?: number) => void;
    device?: DeviceType;
}

function SequenceQuestionElement(props: SequenceQuestionElementProps): ReactElement {
    const {element, elementInteractions, savePresentationRunFile, device = DeviceType.DESKTOP} = props;
    const {answered, answeredSequence} = elementInteractions;
    const {sequence, correctSequence} = element;

    const presentation = useSelector(loadedPresentationSelector);

    const handleTry = useSequenceQuestionTry();
    const {getCommentProps} = useOpenComment();

    const {isWrong} = elementInteractions;

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        if (isWrong) {
            timeout = setTimeout(() => {
                savePresentationRunFile({...elementInteractions, isWrong: false});
            }, 1000);
        }
        return () => clearTimeout(timeout);
    }, [elementInteractions, isWrong, savePresentationRunFile]);

    useEffect(() => {
        if (elementInteractions.markedAnswers) return;

        savePresentationRunFile({...elementInteractions, markedAnswers: sequence});
    }, [elementInteractions, elementInteractions.markedAnswers, savePresentationRunFile, sequence]);

    function swapSequenceItem(newIndex: number, oldIndex: number): void {
        const marked = elementInteractions.markedAnswers;
        if (!marked) return;

        const newSequence = swapArrayIndices(marked, newIndex, oldIndex);

        handleSelectAnswers(elementInteractions, element, newSequence, savePresentationRunFile, presentation);
    }

    const answeredArray = answeredSequence?.map((item) => {
        return item.text;
    });

    const getDraggedStyle = (
        isDragging: boolean,
        draggableStyle: DraggingStyle | NotDraggingStyle | {position: 'absolute'} | undefined
    ): DraggingStyle | NotDraggingStyle | {position: 'absolute'} | undefined => {
        if (isDragging) {
            return {
                ...draggableStyle,
                position: 'absolute',
            };
        }
        return draggableStyle;
    };

    const getAnswerType = (index: number): AnswerTypes => {
        if (elementInteractions.answered && answeredArray) {
            if (element.isDebatable) {
                return AnswerTypes.DEBATABLE;
            }
            if (correctSequence[index] === answeredArray[index]) {
                return AnswerTypes.CORRECT;
            }
            return AnswerTypes.INCORRECT;
        }
        return AnswerTypes.NONE;
    };

    function getAnswers(): JSX.Element {
        return (
            <div>
                <DragDropContext
                    onDragEnd={(e) => {
                        if (typeof e.destination?.index !== 'undefined') swapSequenceItem(e.destination.index, e.source.index);
                    }}
                >
                    <Droppable droppableId={`sequence-question-${element.id}`} isDropDisabled={answered}>
                        {(provided) => {
                            return (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {elementInteractions.markedAnswers?.map((item, index) => {
                                        return (
                                            <Draggable
                                                key={`${index}`}
                                                draggableId={`sequence-question-${index}`}
                                                index={index}
                                                isDragDisabled={answered}
                                            >
                                                {(provided, snapshot) => {
                                                    return (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className="c-draggable c-sequenceQuestionElement__answer c-questionLayout__body"
                                                            key={`element-sequence-${element.id}-${index}`}
                                                            style={getDraggedStyle(snapshot.isDragging, provided.draggableProps.style)}
                                                        >
                                                            <QuestionAnswer
                                                                key={`question-answer-${element.id}-${index}`}
                                                                answer={item.text}
                                                                answerType={AnswerTypes.NONE}
                                                                leadingIcon="akar-icons:drag-vertical"
                                                                correctColor={element.correctColor}
                                                                incorrectColor={element.incorrectColor}
                                                                debatableColor={element.debatableColor}
                                                                styles={getAnswerStyles(device, element)}
                                                                onClick={() => undefined}
                                                                draggable
                                                                device={device}
                                                                {...getCommentProps(index, !!item.comment)}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            );
                        }}
                    </Droppable>
                </DragDropContext>
            </div>
        );
    }

    function getAnswered(): JSX.Element {
        return (
            <div className="c-sequenceQuestionElement__answers">
                {answeredSequence?.map((item, index) => {
                    return (
                        <QuestionAnswer
                            key={`answer-item-${element.id}-${index}`}
                            answer={item.text}
                            answerType={getAnswerType(index)}
                            comment={item.comment}
                            correctColor={element.correctColor}
                            incorrectColor={element.incorrectColor}
                            debatableColor={element.debatableColor}
                            styles={getAnswerStyles(device, element)}
                            onClick={() => undefined}
                            leadingIcon="akar-icons:drag-vertical"
                            device={device}
                            {...getCommentProps(index, !!item.comment)}
                        />
                    );
                })}
            </div>
        );
    }

    return (
        <QuestionLayout
            element={element}
            elementInteractions={elementInteractions}
            device={device}
            showConfirmButton
            onConfirm={() => handleTry(element, elementInteractions)}
            confirmDisabled={answered}
        >
            <div className="c-sequenceQuestionElement">
                <div>{!answered && getAnswers()}</div>
                <div>{answered && getAnswered()}</div>
            </div>
        </QuestionLayout>
    );
}

export default SequenceQuestionElement;
